import { Component, EventEmitter, Input, Output } from '@angular/core';
import { BaseTechnicalSectionComponent } from '../../common/base-technical-section.component';
import { detectDecimalSeparator } from '../../../utils/utils';
import { TechnicalSpecificationsFormSections, UICollectionTypes } from '../../../entities/enums/ui-collection-types.enum';
import { EnvironmentService } from '../../../../services/environment.service';
import { STORAGE_CONNECTIONS } from '../../../constants/grid-application-constants';
import { StorageUnit } from '../../../entities/storageUnit';
import { StoragePowerConverter } from '../../../entities/storagePowerConverter';
import { IntegratedGridProtectionDocument } from '../../../entities/integratedGridProtectionDocument';
import { GridApplicationCommentsService } from '../../../../+grid-application-shared/services/grid-application-comments.service';
import { ControlContainer, NgForm } from '@angular/forms';
import { NetzTranslateService } from '../../../../services/netz-translate.service';

@Component({
  selector: 'app-storage-data',
  templateUrl: './storage-data.component.html',
  styleUrls: ['./storage-data.component.scss'],
  viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
})
export class StorageDataComponent extends BaseTechnicalSectionComponent {
  @Input() powerStorageExists: boolean;
  @Input() isIntegratedGridProtection: boolean;
  @Output() warningsVisible = new EventEmitter<boolean>();

  public readonly UI_COLLECTION_TYPES = UICollectionTypes;
  public readonly detectDecimalSeparator = detectDecimalSeparator;
  showNumberOfCorrespondingFigureAccordingToFnn = this.environmentService.showNumberOfCorrespondingFigureAccordingToFnn;
  storagesPowerNotEqual: boolean;
  maximumDischargeCapacityNotEqual: boolean;

  showFeedInPaymentsRecordedSeparately = this.environmentService.showFeedInPaymentsRecordedSeparately;
  showPrimarySourcesRecordedSeparately = this.environmentService.showPrimarySourcesRecordedSeparately;

  constructor(
    commentService: GridApplicationCommentsService,
    environmentService: EnvironmentService,
    private translate: NetzTranslateService
  ) {
    super(commentService, environmentService);
  }

  public addStorageUnit(): void {
    this.technicalData.storages.push(new StorageUnit());
    this.calculatePowerDifference();
    this.calculateMaximumDischargeCapacityDifference();
  }

  public removeStorageUnit(event: any): void {
    this.technicalData.storages = this.technicalData.storages.filter(x => x !== event);
    this.calculatePowerDifference();
    this.calculateMaximumDischargeCapacityDifference();
  }

  public addPowerConverter(): void {
    this.technicalData.power_converters.push(new StoragePowerConverter());
    this.addGridProtectionDoc(UICollectionTypes.PowerConverter);
  }

  public removePowerConverter(event: any): void {
    const index = this.technicalData.power_converters.indexOf(event.element);
    this.technicalData.power_converters = this.technicalData.power_converters.filter(x => x !== event.element);

    this.removeGridProtectionDoc(index, event.type);
  }

  public storageConnectionChange(event: any): void {
    if (event === STORAGE_CONNECTIONS.AC) {
      if (!this.technicalData.power_converters.length) {
        this.addPowerConverter();
      }

      return;
    }

    this.technicalData.power_converters = [];
    this.removeGridProtectionDocsByType(UICollectionTypes.PowerConverter);
  }

  public calculatePowerDifference(): void {
    const total = this.technicalData.storages.reduce((value, storage) => value + storage.usable_capacity, 0);
    this.storagesPowerNotEqual = +total.toFixed(5) !== +this.technicalData.storage_data.total_capacity.toFixed(5);
    this.warningsVisible.emit(this.isCalculationDifferencedetected());
  }

  public calculateMaximumDischargeCapacityDifference(): void {
    const total = this.technicalData.storages.reduce((value, storage) => value + storage.max_discharge_capacity, 0);
    this.maximumDischargeCapacityNotEqual = +total.toFixed(5) !== +this.technicalData.storage_data.max_discharge_capacity.toFixed(5);
    this.warningsVisible.emit(this.isCalculationDifferencedetected());
  }

  public isEditableByExternalRoles(): boolean {
    return (
      (this.isTechnicianRole() && this.editabilityBasedOnProcessStatus.technicianRole) ||
      (this.isCustomerRole() && this.editabilityBasedOnProcessStatus.customerRole)
    );
  }

  get formSection(): TechnicalSpecificationsFormSections {
    return TechnicalSpecificationsFormSections.DetailedInfoStorage;
  }

  protected get sectionComment(): string | null {
    return this.technicalData.technical_specifications_sections_state?.detailed_info_storage_comment;
  }

  protected set sectionComment(value: string | null) {
    this.technicalData.technical_specifications_sections_state.detailed_info_storage_comment = value;
  }

  protected get sectionLocked(): boolean | null {
    return this.technicalData.technical_specifications_sections_state?.is_detailed_info_storage_locked;
  }

  protected set sectionLocked(value: boolean | null) {
    this.technicalData.technical_specifications_sections_state.is_detailed_info_storage_locked = value;
  }

  // TODO : This would probably be moved in base, but stays here for now
  private addGridProtectionDoc(documentType: string): void {
    if (this.isIntegratedGridProtection) {
      this.technicalData.grid_power_plant_protection_data.integrated_grid_protection_documents.push(
        new IntegratedGridProtectionDocument(documentType)
      );
    }
  }

  private removeGridProtectionDoc(index: number, unitType: string): void {
    if (this.isIntegratedGridProtection) {
      const documentsToFilter = this.technicalData.grid_power_plant_protection_data.integrated_grid_protection_documents.filter(
        d => d.unit_type === unitType
      );

      this.technicalData.grid_power_plant_protection_data.integrated_grid_protection_documents =
        this.technicalData.grid_power_plant_protection_data.integrated_grid_protection_documents.filter(d => d.unit_type !== unitType);

      documentsToFilter.splice(index, 1);

      this.technicalData.grid_power_plant_protection_data.integrated_grid_protection_documents.push(...documentsToFilter);
    }
  }

  private removeGridProtectionDocsByType(documentType: string): void {
    if (this.isIntegratedGridProtection) {
      this.technicalData.grid_power_plant_protection_data.integrated_grid_protection_documents =
        this.technicalData.grid_power_plant_protection_data.integrated_grid_protection_documents.filter(x => x.unit_type !== documentType);
    }
  }

  private isCalculationDifferencedetected(): boolean {
    return this.storagesPowerNotEqual || this.maximumDischargeCapacityNotEqual;
  }
}
